import { shortMonthNames } from './dateUtils';
import type { ChartGranularity } from '@/types';

/**
 * Creates a stateful formatter for Recharts X-axis date ticks: shows the short
 * month name on the first tick of each month, and the zero-padded day otherwise.
 * Expects tick values in YYYY-MM-DD format.
 *
 * @returns A function suitable for use as XAxis tickFormatter.
 */
export const createXAxisDateFormatter = (): ((tick: string, i: number) => string) => {
  let xAxisMonthIndex = '';

  return (tick: string, i: number): string => {
    const [, month, day] = tick.split('-');
    const monthStr = shortMonthNames[parseInt(month, 10) - 1];
    const dayStr = parseInt(day, 10).toString().padStart(2, '0');

    if (monthStr !== xAxisMonthIndex || i === 0) {
      xAxisMonthIndex = monthStr;
      return monthStr;
    }
    return dayStr;
  };
};

/**
 * Returns an X-axis tick formatter appropriate for the given granularity.
 * - daily: delegates to {@link createXAxisDateFormatter} (month name / day).
 * - weekly: "Mon DD" (e.g. "Feb 03").
 * - monthly: short month name only (e.g. "Feb").
 */
export const createGranularityXAxisFormatter = (
  granularity: ChartGranularity
): ((tick: string, i: number) => string) => {
  if (granularity === 'day') return createXAxisDateFormatter();

  return (tick: string): string => {
    const [, month, day] = tick.split('-');
    const monthStr = shortMonthNames[parseInt(month, 10) - 1];
    if (granularity === 'month') return monthStr;
    return `${monthStr} ${parseInt(day, 10).toString().padStart(2, '0')}`;
  };
};
